"use client"

import { VoteStatus, RaffleStatus } from "@repo/loomlock/client"
import {
  IVote,
  IRaffle,
  IGiveaway,
  IGachabox,
  GiveawayStatus,
} from "@repo/loomlock/model-types"
import type { ICLockProduct } from "@repo/loomlock/model-types"
import Link from "next/link"
import { format } from "date-fns"
import { cn } from "@abraxas/ui/lib"
import { Calendar } from "lucide-react"
import { LockVSLock } from "./LockVSLock"
import { CommunityVote } from "./CommunityVote"

interface EventCardProps {
  event:
    | (IVote & { status?: VoteStatus })
    | (IRaffle & { status?: RaffleStatus })
    | (IGiveaway & { status?: GiveawayStatus })
    | IGachabox
  className?: string
}

const isVote = (
  event: IVote | IRaffle | IGiveaway | IGachabox,
): event is IVote => {
  return "voteId" in event || "maxSelectableOptions" in event
}

const isRaffle = (
  event: IVote | IRaffle | IGiveaway | IGachabox,
): event is IRaffle => {
  return "raffleId" in event || "maxTickets" in event
}

const isGiveaway = (
  event: IVote | IRaffle | IGiveaway | IGachabox,
): event is IGiveaway => {
  return "giveawayId" in event || "numWinners" in event
}

const isGachabox = (
  event: IVote | IRaffle | IGiveaway | IGachabox,
): event is IGachabox => {
  return "gachaboxId" in event
}

const getEventType = (event: IVote | IRaffle | IGiveaway | IGachabox) => {
  if (isGachabox(event)) {
    return "GACHABOX"
  }
  if (isGiveaway(event)) {
    return "GIVEAWAY"
  }
  if (isVote(event)) {
    if ((event as IVote).type === "draw") {
      return "GACHABOX"
    }
    return "VOTE"
  }
  return "RAFFLE"
}

const getEventLink = (event: IVote | IRaffle | IGiveaway | IGachabox) => {
  if (isGachabox(event)) {
    return `/gachaboxes/${event.slug}`
  }
  if (isGiveaway(event)) {
    return `/giveaways/${event.slug}`
  }
  if (isVote(event)) {
    if ((event as IVote).type === "draw") {
      return `/draws/${event.slug}`
    }
    return `/votes/${event.slug}`
  }
  return `/clocks/${event.slug}`
}

const getEventImage = (event: IVote | IRaffle | IGiveaway | IGachabox) => {
  if (isGachabox(event)) {
    return (
      event.ogImage ||
      "https://lock.nyc3.cdn.digitaloceanspaces.com/assets/illustrations/gachabox-left.webp"
    )
  }
  if (isVote(event) && event.ogImage) {
    return event.ogImage
  }
  if (isRaffle(event)) {
    return (
      (event.product as ICLockProduct).imageNoBg ||
      (event.product as ICLockProduct).metadata.image
    )
  }
  if (isGiveaway(event) && event.ogImage) {
    return event.ogImage
  }
  return "/placeholder-lock.png"
}

export function EventCard({ event, className }: EventCardProps) {
  const eventType = getEventType(event)
  const eventLink = getEventLink(event)
  const eventImage = getEventImage(event)
  const startDate = new Date(event.startTime)
  const endDate = event.endTime ? new Date(event.endTime) : null

  return (
    <Link
      href={eventLink}
      className={cn(
        "bg-brand-blue-700 flex flex-col md:flex-row gap-4 items-center pl-[116px] pr-3 py-3 rounded-xl shadow-xl relative",
        className,
      )}
    >
      <div className="absolute bg-brand-blue-600 flex flex-col items-center left-5 pb-2 pt-5 px-2 rounded-b-2xl text-center text-white top-0 w-[76px]">
        <p className="font-medium text-body-s w-full uppercase">
          {format(startDate, "MMM")}
        </p>
        <p className="font-semibold text-body-l w-full">
          {format(startDate, "dd")}
        </p>
      </div>

      <div className="flex-1 flex flex-col gap-2 items-start justify-center min-w-0">
        <div className="flex flex-col gap-2 items-start w-full">
          <div className="bg-brand-blue-500 flex gap-2 items-center justify-center px-2 py-1 rounded-sm">
            <p className="font-semibold text-m_body-xs text-accent-yellow whitespace-pre">
              {eventType}
            </p>
          </div>
          <p className="text-body-m text-white">{event.name}</p>
          {eventType === "RAFFLE" && (
            <p className="text-body-m">
              {(event as IRaffle).quantity} limited units!
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="border-neutral-grey-400 border-[0.5px] flex gap-2 items-center justify-center px-3 py-1 rounded-full">
            <Calendar className="shrink-0 h-5" />
            <p className="font-medium text-body-xs text-white whitespace-pre">
              {endDate
                ? `${format(
                    startDate,
                    startDate.getMonth() === endDate.getMonth()
                      ? "dd"
                      : "dd MMMM",
                  )} - ${format(endDate, "dd MMMM")}`
                : format(startDate, "dd MMMM")}
            </p>
          </div>
          {/* {getStatusBadge(event.status)} */}
        </div>
      </div>

      <div className="flex gap-2 max-h-[132px] h-full w-[200px]">
        {eventType === "VOTE" ? (
          (event as IVote).type === "vs" ? (
            <LockVSLock />
          ) : (
            <CommunityVote />
          )
        ) : (
          <img src={eventImage} alt={event.name} className="object-contain" />
        )}
      </div>
    </Link>
  )
}
